import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';

// Embedded dashboard for UMT-HUD iframe view
// This is a read-only, anonymous view of the dashboard stats

interface WidgetData {
  status: 'healthy' | 'warning' | 'critical' | 'error';
  lastUpdated: string;
  stats: Array<{
    label: string;
    value: string | number;
    type?: 'info' | 'success' | 'warning' | 'critical';
  }>;
  alerts: Array<{
    severity: 'info' | 'warning' | 'critical';
    message: string;
    timestamp: string;
  }>;
}

export default function EmbeddedDashboard() {
  const [searchParams] = useSearchParams();
  const theme = searchParams.get('theme') || 'dark';
  const [data, setData] = useState<WidgetData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Apply theme
  useEffect(() => {
    document.documentElement.setAttribute('data-theme', theme);
    document.documentElement.classList.toggle('dark', theme === 'dark');
  }, [theme]);

  // Fetch widget data
  useEffect(() => {
    async function fetchData() {
      try {
        const response = await fetch('/api/widget-summary');
        if (!response.ok) throw new Error('Failed to fetch data');
        const result = await response.json();
        setData(result);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Unknown error');
      } finally {
        setLoading(false);
      }
    }
    fetchData();
    // Refresh every 60 seconds
    const interval = setInterval(fetchData, 60000);
    return () => clearInterval(interval);
  }, []);

  const getStatColor = (type?: string) => {
    switch (type) {
      case 'success': return 'text-green-600 dark:text-green-400';
      case 'warning': return 'text-yellow-600 dark:text-yellow-400';
      case 'critical': return 'text-red-600 dark:text-red-400';
      default: return 'text-blue-600 dark:text-blue-400';
    }
  };

  const getSeverityBadge = (severity: string) => {
    switch (severity) {
      case 'critical': return 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200';
      case 'warning': return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200';
      default: return 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200';
    }
  };

  const formatTime = (timestamp: string) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diff = now.getTime() - date.getTime();
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));
    const hours = Math.floor(diff / (1000 * 60 * 60));
    const minutes = Math.floor(diff / (1000 * 60));

    if (days > 0) return `${days}d ago`;
    if (hours > 0) return `${hours}h ago`;
    if (minutes > 0) return `${minutes}m ago`;
    return 'Just now';
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-muted">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-muted">
        <div className="text-center text-red-600 dark:text-red-400">
          <p>Failed to load dashboard</p>
          <p className="text-sm text-muted-foreground">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-muted p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <span className="text-2xl">☁️</span>
          <h1 className="text-xl font-bold text-foreground">
            365 Management
          </h1>
          <span className={`px-2 py-1 rounded-full text-xs font-medium ${
            data.status === 'healthy' ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200' :
            data.status === 'warning' ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200' :
            'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
          }`}>
            {data.status.toUpperCase()}
          </span>
        </div>
        <div className="text-sm text-muted-foreground">
          Updated: {formatTime(data.lastUpdated)}
        </div>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        {data.stats.map((stat, i) => (
          <div key={i} className="bg-card rounded-lg p-4 shadow-sm border border-border">
            <div className="text-sm text-muted-foreground mb-1">
              {stat.label}
            </div>
            <div className={`text-2xl font-bold ${getStatColor(stat.type)}`}>
              {stat.value}
            </div>
          </div>
        ))}
      </div>

      {/* Alerts */}
      <div className="bg-card rounded-lg shadow-sm border border-border">
        <div className="px-4 py-3 border-b border-border">
          <h2 className="font-semibold text-foreground">Recent Alerts</h2>
        </div>
        <div className="divide-y divide-border">
          {data.alerts.length === 0 ? (
            <div className="p-4 text-center text-muted-foreground">
              No active alerts
            </div>
          ) : (
            data.alerts.slice(0, 10).map((alert, i) => (
              <div key={i} className="p-4 flex items-start gap-3">
                <span className={`px-2 py-0.5 rounded text-xs font-medium ${getSeverityBadge(alert.severity)}`}>
                  {alert.severity}
                </span>
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-foreground truncate">
                    {alert.message}
                  </p>
                  <p className="text-xs text-muted-foreground mt-1">
                    {formatTime(alert.timestamp)}
                  </p>
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      {/* Footer */}
      <div className="mt-6 text-center text-xs text-muted-foreground">
        Read-only view &bull; Sign in for full access
      </div>
    </div>
  );
}
